import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Dialog, Stack, TextField, Theme } from '@mui/material';

import { ReactComponent as CancelAwbIcon } from '@/assets/icons/cancel-awb-icon.svg';
import AppButton from '@/shared/ui/AppButton';
import { COMPACT_FORM_LABEL_SX, buildCompactInputSx } from '@/shared/ui/form-fields/fieldStyles';
import { FormLabel } from '@/shared/ui/styled';

import { useVoidShipmentMutation } from '../../api/useShippingMutations';
import AwbDialogHeader from './AwbDialogHeader';
import AwbNotice from './awb/AwbNotice';
import { generateAwbResultPaperSx } from './styles';

const NOTICE_SX = {
  px: '0.5rem',
  backgroundColor: 'transparent',
  color: (theme: Theme) => theme.palette.grey[800],
  '& svg': { color: (theme: Theme) => theme.palette.blue[500] },
};

const REASON_INPUT_SX = buildCompactInputSx(false, { paddingBottom: 0 });

interface VoidShipmentDialogProps {
  open: boolean;
  shipmentId: string;
  awbNumber?: string;
  recipientLabel?: string;
  onClose: () => void;
}

const VoidShipmentDialog: FC<VoidShipmentDialogProps> = ({
  open,
  shipmentId,
  awbNumber,
  recipientLabel,
  onClose,
}) => {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const { mutateAsync, isLoading } = useVoidShipmentMutation();

  const subtitle = [awbNumber && t('awb.awb_label', { awb: awbNumber }), recipientLabel]
    .filter(Boolean)
    .join(' · ');

  const handleClose = () => {
    setReason('');
    onClose();
  };

  const handleConfirm = async () => {
    await mutateAsync({ id: shipmentId, reason: reason.trim() || undefined });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth={false}
      PaperProps={{
        'data-testid': 'void-shipment-dialog',
        sx: { ...generateAwbResultPaperSx, maxWidth: '32.875rem' },
      }}
    >
      <AwbDialogHeader
        icon={<CancelAwbIcon />}
        tone="error"
        title={t('awb.cancel_title')}
        subtitle={subtitle}
        subtitleTestId="void-shipment-subtitle"
        onClose={handleClose}
        closeTestId="void-shipment-close"
      />

      <Stack>
        <FormLabel sx={COMPACT_FORM_LABEL_SX}>{t('Reason')}</FormLabel>
        <TextField
          size="small"
          fullWidth
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('Optional')}
          sx={REASON_INPUT_SX}
          inputProps={{ maxLength: 255, 'data-testid': 'void-shipment-reason' }}
        />
      </Stack>

      <AwbNotice tone="info" sx={NOTICE_SX} data-testid="void-shipment-notice">
        {t('awb.cancel_notice')}
      </AwbNotice>

      <Stack direction="row" justifyContent="flex-end" gap="0.5rem">
        <AppButton
          variant="secondary"
          onClick={handleClose}
          disabled={isLoading}
          data-testid="void-shipment-keep"
        >
          {t('awb.keep_awb')}
        </AppButton>
        <AppButton
          variant="p-alert"
          loading={isLoading}
          onClick={handleConfirm}
          data-testid="void-shipment-confirm"
        >
          {t('awb.cancel_title')}
        </AppButton>
      </Stack>
    </Dialog>
  );
};

export default VoidShipmentDialog;
